/**
 * Locomotion (RL walking) setup.
 *
 * Training needs three things the assembly does not say on its own: the
 * stance the robot should stand in (the home pose), which way is forward,
 * and which links are feet. All three live on the server; this panel edits
 * them over the websocket and shows what came back. The training itself
 * runs on the user's own machine, started by the curl command at the
 * bottom, and uploads its results to this server when it finishes.
 */

import { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Code,
  Group,
  NumberInput,
  SegmentedControl,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";

import {
  useInstances,
  useLocomotionBaseHeight,
  useLocomotionDetectedFeet,
  useLocomotionError,
  useLocomotionFeet,
  useLocomotionForwardWorld,
  useLocomotionHomePose,
  useLocomotionLinkReach,
  useLocomotionLoading,
  useViewerStore,
} from "../../store";
import { useWebSocket } from "../../WebSocket";
import { CopyCommandButton } from "../../components/CopyCommandButton";

const FORWARD_OPTIONS: {
  value: string;
  label: string;
  vector: [number, number, number];
}[] = [
  { value: "+x", label: "+X", vector: [1, 0, 0] },
  { value: "-x", label: "-X", vector: [-1, 0, 0] },
  { value: "+y", label: "+Y", vector: [0, 1, 0] },
  { value: "-y", label: "-Y", vector: [0, -1, 0] },
];

const CUSTOM_FORWARD = "custom";
const DEFAULT_ITERATIONS = 1500;
/** The header's package name is local to it, so the launcher uses the same default. */
const DEFAULT_PACKAGE_NAME = "assembled_robot";

/** Which of the four axis choices a forward vector is, or "custom" when it is none of them. */
export function forwardOptionValue(forward: number[]): string {
  const match = FORWARD_OPTIONS.find((option) =>
    option.vector.every(
      (component, index) => Math.abs((forward[index] ?? 0) - component) < 1e-6,
    ),
  );
  return match ? match.value : CUSTOM_FORWARD;
}

/** "leg_fl/foot" -> ["leg_fl", "foot"]; a bare name has no prefix. */
function splitLinkName(name: string): [string | null, string] {
  const slash = name.lastIndexOf("/");
  if (slash < 0) {
    return [null, name];
  }
  return [name.slice(0, slash), name.slice(slash + 1)];
}

export function buildLocomotionLaunchCommand(
  origin: string,
  sessionId: string,
  iterations: number,
): string {
  const query = [
    `package_name=${encodeURIComponent(DEFAULT_PACKAGE_NAME)}`,
    "launch_command=locomotion",
    `session=${encodeURIComponent(sessionId)}`,
    `iters=${iterations}`,
  ].join("&");
  return `curl -s -L "${origin}/api/launch-it?${query}" | bash`;
}

export function LocomotionSection() {
  const instances = useInstances();
  const homePose = useLocomotionHomePose();
  const forwardWorld = useLocomotionForwardWorld();
  const feet = useLocomotionFeet();
  const detectedFeet = useLocomotionDetectedFeet();
  const linkReach = useLocomotionLinkReach();
  const baseHeight = useLocomotionBaseHeight();
  const error = useLocomotionError();
  const loading = useLocomotionLoading();
  const setLocomotionLoading = useViewerStore(
    (state) => state.setLocomotionLoading,
  );
  const { sendUpdateLocomotion, isConnected } = useWebSocket();
  const [iterations, setIterations] = useState<number>(DEFAULT_ITERATIONS);

  // The server recomputes reach and detected feet from the assembly, so
  // a module coming or going is a reason to ask again; a plain re-send of
  // the same list is not. The sender is memoised per connection, so a
  // reconnect asks again too, which is what a fresh server wants.
  //
  // Each request carries the list it was asked for, sorted: a config
  // load adds modules one message at a time, and the server drops the
  // requests whose list is already out of date instead of exporting the
  // assembly for each of them. While the socket is down there is nobody
  // to ask; the connection coming back re-runs this and asks then.
  const instanceKey = instances
    .map((instance) => instance.instance_id)
    .sort()
    .join("|");
  useEffect(() => {
    if (!isConnected) return;
    sendUpdateLocomotion({ instances_key: instanceKey });
  }, [instanceKey, sendUpdateLocomotion, isConnected]);

  const request = (fields: Parameters<typeof sendUpdateLocomotion>[0]) => {
    setLocomotionLoading(true);
    sendUpdateLocomotion(fields);
  };

  const homePoseCount = Object.keys(homePose).length;
  const forwardValue = forwardOptionValue(forwardWorld);
  const forwardData = useMemo(
    () =>
      forwardValue === CUSTOM_FORWARD
        ? [
            ...FORWARD_OPTIONS,
            { value: CUSTOM_FORWARD, label: "custom", disabled: true },
          ]
        : FORWARD_OPTIONS,
    [forwardValue],
  );
  const feetSet = useMemo(() => new Set(feet), [feet]);
  const detectedSet = useMemo(() => new Set(detectedFeet), [detectedFeet]);

  const handleForwardChange = (value: string) => {
    const option = FORWARD_OPTIONS.find(
      (candidate) => candidate.value === value,
    );
    if (option) {
      request({ forward_world: option.vector });
    }
  };

  const handleFootToggle = (link: string, checked: boolean) => {
    const next = checked
      ? [...feet.filter((name) => name !== link), link]
      : feet.filter((name) => name !== link);
    request({ feet: next });
  };

  const sessionId = window.sessionId ?? "";
  const launchCommand = buildLocomotionLaunchCommand(
    window.location.origin,
    sessionId,
    iterations,
  );

  return (
    <Stack gap="sm">
      <Box>
        <Text size="xs" fw={600}>
          Home pose
        </Text>
        <Text size="xs" c="dimmed">
          {homePoseCount > 0
            ? `Home pose: saved (${homePoseCount} joints)`
            : "Home pose: not saved (current pose will be used)"}
        </Text>
        <Group gap="xs" mt={4}>
          <Tooltip
            label="Pose the robot with the joint sliders in the stance it should stand in, then save."
            multiline
            w={220}
          >
            <Button
              size="xs"
              variant="light"
              disabled={loading}
              onClick={() => request({ set_home_pose_from_current: true })}
            >
              Save current pose as home
            </Button>
          </Tooltip>
          <Button
            size="xs"
            variant="subtle"
            color="gray"
            disabled={homePoseCount === 0}
            onClick={() => request({ clear_home_pose: true })}
          >
            Clear
          </Button>
        </Group>
      </Box>

      <Box>
        <Text size="xs" fw={600}>
          Forward direction (world)
        </Text>
        <SegmentedControl
          size="xs"
          fullWidth
          value={forwardValue}
          onChange={handleForwardChange}
          data={forwardData}
          aria-label="Forward direction"
        />
      </Box>

      <Box>
        <Group justify="space-between" wrap="nowrap">
          <Text size="xs" fw={600}>
            Feet
          </Text>
          <Button
            size="compact-xs"
            variant="light"
            disabled={loading}
            onClick={() => request({ auto_detect_feet: true })}
          >
            Auto-detect
          </Button>
        </Group>
        {linkReach.length === 0 ? (
          <Text size="xs" c="dimmed">
            No links with geometry yet.
          </Text>
        ) : (
          <Stack gap={2} mt={4}>
            {linkReach.map((entry) => {
              const [prefix, shortName] = splitLinkName(entry.link);
              return (
                <Checkbox
                  key={entry.link}
                  size="xs"
                  checked={feetSet.has(entry.link)}
                  onChange={(event) =>
                    handleFootToggle(entry.link, event.currentTarget.checked)
                  }
                  aria-label={entry.link}
                  label={
                    <Group gap={4} wrap="nowrap">
                      <Text
                        size="xs"
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={entry.link}
                      >
                        {prefix !== null && (
                          <Text span size="xs" c="dimmed">
                            {prefix}/
                          </Text>
                        )}
                        {shortName}
                      </Text>
                      <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
                        {(entry.min_z * 1000).toFixed(0)} mm
                      </Text>
                      {detectedSet.has(entry.link) && (
                        <Badge
                          size="xs"
                          variant="light"
                          color="teal"
                          style={{ flexShrink: 0 }}
                        >
                          detected
                        </Badge>
                      )}
                    </Group>
                  }
                />
              );
            })}
          </Stack>
        )}
        {baseHeight != null && (
          <Text size="xs" c="dimmed" mt={4}>
            Standing height: {baseHeight.toFixed(3)} m
          </Text>
        )}
        {error && (
          <Text size="xs" c="red" mt={4}>
            {error}
          </Text>
        )}
      </Box>

      <Box>
        <Text size="xs" fw={600}>
          Train
        </Text>
        <Text size="xs" c="dimmed">
          Runs on your own machine. An NVIDIA GPU makes it reasonably fast; a
          CPU works but slowly. Produces walk.mp4 and policy.onnx and uploads
          them back here.
        </Text>
        <NumberInput
          size="xs"
          mt={4}
          label="Iterations"
          min={1}
          max={100000}
          step={100}
          value={iterations}
          onChange={(value) =>
            setIterations(
              typeof value === "number" && value > 0
                ? Math.round(value)
                : DEFAULT_ITERATIONS,
            )
          }
        />
        <Code
          block
          mt={4}
          style={{
            whiteSpace: "pre-wrap",
            wordBreak: "break-all",
            fontSize: 11,
          }}
        >
          {launchCommand}
        </Code>
        <Group mt={4}>
          <CopyCommandButton value={launchCommand} label="Copy train command" />
        </Group>
      </Box>
    </Stack>
  );
}
